import { useEffect } from 'react'
import { Alert, Form, Modal, Select, Space, Tag, Typography } from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { fetchVenues } from '@/features/venue/venueApi'
import { convertLeadToVenue, type Lead } from './crmApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

/**
 * "Yutildi" — lidni OB'EKTGA (mehmonxona, kafe, kovorking) bog'lash.
 *
 * <h2>Nega ALOHIDA oyna, `LeadWonModal` ichidagi uchinchi tanlov emas</h2>
 *
 * Backendda ikki nishon bor va ular ATAYLAB har xil naqshda yozilgan
 * (`LeadResource` javadoc'i):
 *
 * <ul>
 *   <li><b>abonent</b> — `TenantWideGuard`, chunki `xsib-crm` da
 *       abonentga egalik ustuni YO'Q; panel oldin hisob YARATISHI
 *       mumkin (`subscriber.create`), ya'ni ikki tarmoq chaqiruvi va
 *       oradagi "yaratildi, lekin bog'lanmadi" holati;</li>
 *   <li><b>ob'ekt</b> — HAQIQIY doira predikati (`VENUE` doirasining
 *       `scope_id` i aynan ob'ektning o'zi), bitta chaqiruv, hech
 *       qanday oraliq holat yo'q.</li>
 * </ul>
 *
 * Ya'ni ikkalasi bitta `Segmented` ostiga qo'yilganda foydalanuvchi bir
 * xil ko'rinadigan, lekin boshqacha yiqiladigan ikki mexanizmni
 * ko'rardi. `LeadWonModal` dagi `orphanSubscriber` holati esa butun
 * oyna tanasini qisqa tutashtiradi (`orphanSubscriber ? null : ...`) —
 * uchinchi rejim o'sha shoxdan o'tishi kerak bo'lardi va abonentga
 * tegishli "bog'lashni qayta urinish" tugmasi ob'ekt yo'lida ham
 * chiqib qolardi.
 *
 * ⚠️ Ikkinchi sabab — REGRESSIYA. Abonent yo'li ishlaydi va u tashqi
 * tekshiruvchining IJOBIY natijalari ro'yxatida. Alohida fayl uni
 * qurilish jihatidan tegilmagan qoldiradi.
 *
 * <h2>⚠️ Ob'ekt bu yerda YARATILMAYDI — abonentdan FARQLI</h2>
 *
 * `LeadWonModal` yangi abonentni o'zi ocha oladi, chunki abonent uchun
 * login va parol yetadi. Ob'ekt esa yaratilganda HUQUQIY qaror talab
 * qiladi: `piiRetentionDays` va `journalRetentionDays` (mehmon jurnali
 * saqlash muddati) hamda keyin O'ZGARTIRIB BO'LMAYDIGAN `code`. Buni
 * savdo oynasining ichiga yashirish — operatorni o'zi bilmagan holda
 * huquqiy sozlama tanlashga majburlash bo'lardi. Shuning uchun bu yerda
 * faqat BOG'LASH bor, yaratish esa "Ob'ektlar" sahifasida qoladi
 * (`VenueFormModal`).
 *
 * <h2>⚠️ YOPILGAN ob'ekt tanlanmaydi</h2>
 *
 * `WON` — terminal holat: `lead_state_transition_guard` undan chiqishga
 * yo'l qo'ymaydi va `LeadServiceImpl#transition` qayta konvertatsiyani
 * bloklaydi. Ya'ni noto'g'ri biriktirilgan ob'ekt ABADIY qoladi.
 * `CLOSED` ob'ekt esa qaytarib bo'lmaydigan holatda ("Yopilgan —
 * qaytarib bo'lmaydi", `VenueState`), unga yangi yutuqni bog'lash
 * ma'nosiz. Shuning uchun u ro'yxatda KO'RINADI, lekin TANLANMAYDI —
 * butunlay yashirish "ob'ektim qani?" degan javobsiz savol berardi.
 *
 * <p>`SUSPENDED` esa tanlanadi: to'xtatish VAQTINCHA va ob'ekt
 * qaytarilishi mumkin.
 *
 * <h2>⚠️ Xato MUTATSIYADA qoladi</h2>
 *
 * `onError` + alohida `useState` naqshi ATAYLAB ishlatilmadi: xato
 * ikki joyda yashaganda uni qo'lda tozalash kerak bo'ladi va bir joyda
 * unutilsa keyingi ochilishda BEGONA xabar chiqadi
 * (`LeadLostModal`/`VoucherVoidModal` naqshi, `tests/guards.mjs`
 * 1-bo'limi).
 */
interface LeadWonVenueModalProps {
  /** `null` — modal yopiq */
  lead: Lead | null
  onCancel: () => void
  /** Muvaffaqiyatli yakun — chaqiruvchi ro'yxatni yangilaydi */
  onDone: () => void
}

interface WonVenueValues {
  venueId: string
}

export function LeadWonVenueModal({ lead, onCancel, onDone }: LeadWonVenueModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<WonVenueValues>()

  // Modal har ochilganda holat toza bo'lsin — aks holda oldingi lidning
  // tanlovi yangisiga o'tib ketardi va bu jimgina noto'g'ri bog'lanish.
  useEffect(() => {
    if (lead) form.resetFields()
  }, [lead, form])

  /*
    ⚠️ Holat filtri YO'Q (`null`) — ataylab. `state: 'ACTIVE'` bilan
    so'ralganda to'xtatilgan ob'ekt ro'yxatdan JIMGINA tushib qolardi va
    operator "ob'ektim yo'q" degan noto'g'ri xulosaga kelardi. Bu yerda
    hammasi ko'rinadi, tanlash esa quyida cheklanadi.
  */
  const venues = useQuery({
    queryKey: queryKeys.venues.list(null),
    queryFn: () => fetchVenues(null),
    enabled: lead !== null,
  })

  const link = useMutation({
    mutationFn: (venueId: string) => convertLeadToVenue(lead!.id, venueId),
    onSuccess: onDone,
  })

  const close = () => {
    link.reset()
    onCancel()
  }

  return (
    <Modal
      open={lead !== null}
      title={t('crm.leadWonVenue.title')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => link.mutate(values.venueId))}
      confirmLoading={link.isPending}
      okText={t('crm.leadWonVenue.confirm')}
      cancelText={t('common.cancel')}
      width={520}
      destroyOnHidden
    >
      {lead ? (
        <Typography.Paragraph type="secondary">
          {t('crm.leadWonVenue.subtitle', { name: lead.companyName || lead.fullName })}
        </Typography.Paragraph>
      ) : null}

      {link.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(link.error, t)}
        />
      ) : null}

      {/* ⚠️ Ro'yxat olinmasa BO'SH tanlov o'rniga sabab ko'rsatiladi:
          jimgina bo'sh ro'yxat "ob'ekt yo'q" bilan "ro'yxatni olib
          bo'lmadi" ni ajratmasdi. */}
      {venues.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(venues.error, t)}
        />
      ) : null}

      <Form<WonVenueValues> form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="venueId"
          label={t('crm.leadWonVenue.pickLabel')}
          extra={t('crm.leadWonVenue.pickHint')}
          rules={[{ required: true, message: t('crm.leadWonVenue.pickRequired') }]}
        >
          <Select
            showSearch
            allowClear
            autoFocus
            optionFilterProp="label"
            loading={venues.isPending}
            placeholder={t('crm.leadWonVenue.searchPlaceholder')}
            notFoundContent={
              venues.isPending ? t('common.loading') : t('crm.leadWonVenue.searchEmpty')
            }
            options={(venues.data ?? []).map((venue) => ({
              value: venue.id,
              // ⚠️ `label` — SOF matn: `optionFilterProp="label"` shu qator
              // bo'yicha qidiradi. Yorliqni bu yerga qo'shsak qidiruv
              // holat nomi bo'yicha ham "topib" qo'yardi.
              label: `${venue.name} (${venue.code})`,
              // ⚠️ Yopilgan ob'ekt — KO'RINADI, lekin tanlanmaydi
              // (klass javadoc'i: WON terminal, xato abadiy qoladi).
              disabled: venue.state === 'CLOSED',
            }))}
            optionRender={(option) => {
              const venue = (venues.data ?? []).find((v) => v.id === option.value)
              return (
                <Space size={6}>
                  <span>{option.label}</span>
                  {venue && venue.state !== 'ACTIVE' ? (
                    <Tag color={venue.state === 'CLOSED' ? 'default' : 'orange'}>
                      {t(`venueState.${venue.state}`)}
                    </Tag>
                  ) : null}
                </Space>
              )
            }}
          />
        </Form.Item>
      </Form>

      {/* ⚠️ Ob'ekt bu oynada YARATILMAYDI (klass javadoc'i) — lekin
          operator buni BILISHI kerak, aks holda bo'sh ro'yxat oldida
          nima qilishni tushunmasdi. */}
      <Alert type="info" showIcon message={t('crm.leadWonVenue.createHint')} />
    </Modal>
  )
}
